import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useSaveStaleAuditDays, useStaleAuditDays } from "@/hooks/use-app-settings";
import { MAX_STALE_AUDIT_DAYS } from "@/lib/stale-audit";

const schema = z.object({
  days: z
    .string()
    .trim()
    .regex(/^\d+$/, "Enter a whole number of days")
    .refine(
      (v) => Number(v) >= 1 && Number(v) <= MAX_STALE_AUDIT_DAYS,
      `Enter a number from 1 to ${MAX_STALE_AUDIT_DAYS}`,
    ),
});

type FormValues = z.infer<typeof schema>;

export function StaleAuditSection() {
  const { data, isLoading, isError, refetch } = useStaleAuditDays();
  const save = useSaveStaleAuditDays();

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { days: "" } });

  useEffect(() => {
    if (data !== undefined) form.reset({ days: String(data) });
  }, [data, form]);

  if (isError) {
    return (
      <ErrorState message="Failed to load the attention threshold" onRetry={() => refetch()} />
    );
  }

  async function onSubmit(values: FormValues) {
    await save.mutateAsync(Number(values.days));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit Attention</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-6">
          Flag an unfinished audit for the Compliance Manager when nothing on it has changed for
          this long. Any note, attachment, checklist change, status change or reassignment counts as
          activity.
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="days"
              render={({ field }) => (
                <FormItem className="max-w-xs">
                  <FormLabel>Days without activity</FormLabel>
                  <FormControl>
                    <Input inputMode="numeric" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormDescription>Default is 14 days.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isLoading || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
